import { useEffect, useState, type ComponentProps } from "react";
import { Button as AriaButton } from "react-aria-components";
import { useIntl } from "react-intl";
import PassengerIcon from "src/design-system/icons/Passenger.svg?react";
import { useScreenMinWidth } from "src/utils/hooks/useScreenMinWidth";
import { type PassengerDetails } from "src/PassengerDetailsProvider";
import { isValidPassengerDetails } from "src/domain/SegmentScreen/SegmentHeader/PassengerPicker/PassengerDetailsValidation";
import { PassengerPickerForm } from "src/domain/SegmentScreen/SegmentHeader/PassengerPicker/PassengerPickerForm/PassengerPickerForm";
import { Button } from "src/design-system/components/Button/Button";
import { sendAnalyticsInteractionEvent } from "src/analytics/sendAnalyticsEvent";
import passengerPickerMessage from "src/domain/SegmentScreen/SegmentHeader/PassengerPicker/PassengerPicker.message";
import XCircleSolid from "src/design-system/icons/XCircleSolid.svg?react";
import { tw } from "src/design-system/tw";
import { useFeature } from "src/feature/useFeature";
import { useSessionStorage } from "src/utils/hooks/useSessionStorage";
import { BlurAwayListener } from "../../../../../components/BlurAwayListener/BlurAwayListener";
import { SearchDatesPicker } from "../../../SearchDatesPicker/SearchDatesPicker";
import messages from "../CombinedPicker.messages";
import DatePopout from "../DatePopout";
import advanceReturnDate from "../utils/advanceReturnDate";
import DatePopoutHeader from "../DatePopoutHeader";
import DatePopoutFooter from "../DatePopoutFooter";
import PassengerPickerPopout from "../PassengerPickerPopout";
import DateInput from "./DateInput";

type Props = {
  className?: string;

  /** Dates */
  beginDate: Date;
  endDate?: Date;

  /** Text */
  beginLabel: string;
  endLabel: string;
  promptEndText: string;
  promptBeginText: string;

  /** Conditionally show the blinking icon */
  initialBlinkDeparture?: boolean;

  /** Callbacks */
  onDatesChange: ComponentProps<typeof SearchDatesPicker>["onDatesChange"];
  onRemoveEndDate?: () => void;
  onSelectEndDate?: ComponentProps<typeof SearchDatesPicker>["onClickDay"];
  onPassengerChange?: (passengerDetails: PassengerDetails) => void;
  onBeginDateToggle: (isOpening: boolean) => void;
  onEndDateToggle: (isOpening: boolean) => void;
  onPassengerToggle?: (isOpening: boolean) => void;

  /** Days available to the calendar */
  maxDaysAhead?: number;

  /** Initial passenger details */
  passengerDetails?: PassengerDetails;
};

/**
 * CombinedPicker
 *
 * Combines the departure and (optional) return date pickers into one component.
 * It also includes a passenger picker.
 */
export default function SearchCombinedPicker({
  className,
  initialBlinkDeparture = true,
  beginDate,
  endDate,
  beginLabel,
  endLabel,
  onDatesChange,
  maxDaysAhead,
  onPassengerChange,
  passengerDetails,
  onSelectEndDate,
  onBeginDateToggle,
  onEndDateToggle,
  onPassengerToggle,
  onRemoveEndDate,
  promptBeginText,
  promptEndText,
}: Props) {
  const intl = useIntl();
  const isAutoPopupEnabled = useFeature("DatesSearchBar") === "VisibleAuto";
  const sessionStorage = useSessionStorage();
  const hasSeenAutoPopup = sessionStorage.getItem("hasSeenDateAutoPopup");
  const [hadEndDate, setHadEndDate] = useState(!!endDate);
  const [isDepartureOpen, setIsDepartureOpen] = useState(
    isAutoPopupEnabled && !hasSeenAutoPopup,
  );
  const [isReturnOpen, setIsReturnOpen] = useState(false);
  const [isPassengerPickerOpen, setIsPassengerPickerOpen] = useState(false);
  const [passengers, setPassengers] = useState(passengerDetails);
  const [blinkDeparture, setBlinkDeparture] = useState(
    !isAutoPopupEnabled && initialBlinkDeparture,
  );
  const { isMdScreen } = useScreenMinWidth();

  /**
   * Will set the return date to a specified date, and then open the date picker.
   */
  function presetReturnAndOpenDatePicker() {
    onDatesChange(beginDate, advanceReturnDate(beginDate, endDate));
    setBlinkDeparture(false);
    setIsReturnOpen(true);
    setIsDepartureOpen(false);
    onEndDateToggle(true);
  }

  const datePopoutHeaderProps: ComponentProps<typeof DatePopoutHeader> = {
    isReturnable: true,
    departureDate: beginDate,
    returnDate: endDate,
    departureInputText: promptBeginText,
    returnInputText: promptEndText,
    handleReturnInputClick: presetReturnAndOpenDatePicker,
    handleDepartureInputClick: () => {
      setBlinkDeparture(false);
      setIsReturnOpen(false);
      setIsDepartureOpen(true);
    },
    handleReturnRemoveClick: () => {
      onDatesChange(beginDate, undefined);
      setHadEndDate(false);
      setIsDepartureOpen(true);
      setIsReturnOpen(false);
    },
    handleCloseButtonClick: (inputOpen) => {
      setBlinkDeparture(false);
      if (inputOpen === "departure") {
        setIsDepartureOpen(false);
        onBeginDateToggle(false);
      }
      if (inputOpen === "return") {
        setIsReturnOpen(false);
        onEndDateToggle(false);
      }
    },
    inputOpen: "departure",
    blinkDeparture,
  };

  useEffect(() => {
    if (!isDepartureOpen && !hasSeenAutoPopup) {
      sessionStorage.setItem("hasSeenDateAutoPopup", "true");
    }
  }, [isDepartureOpen, hasSeenAutoPopup, sessionStorage]);

  return (
    <>
      {isAutoPopupEnabled && !hasSeenAutoPopup && isDepartureOpen && (
        <div className="fixed inset-0 -z-1 bg-black opacity-15"></div>
      )}
      <div className={tw("flex items-center", className)}>
        <span className="flex h-6 min-w-px flex-0 bg-text-secondary opacity-10" />

        <BlurAwayListener
          className="rounded-full"
          blurAfterClick
          immediateFocus={isAutoPopupEnabled && !hasSeenAutoPopup}
          onBlurAway={() => {
            if (isMdScreen) {
              setIsDepartureOpen(false);
              onBeginDateToggle(false);
            }
          }}
        >
          <DateInput
            label={beginLabel}
            date={beginDate}
            isOpen={isDepartureOpen}
            text={intl.formatMessage(messages.placeholder)}
            buttonProps={{
              "aria-label": promptBeginText,
              onPress: () => {
                setIsDepartureOpen((prev) => {
                  onBeginDateToggle(!prev);
                  return !prev;
                });
                setBlinkDeparture(false);
              },
            }}
            blink={blinkDeparture}
          />
          <DatePopout
            anchor="center"
            className="top-full"
            label={promptBeginText}
            isOpen={isDepartureOpen}
          >
            {!isMdScreen && <DatePopoutHeader {...datePopoutHeaderProps} />}
            {isAutoPopupEnabled && !hasSeenAutoPopup && (
              <h4 className="-mt-1 mb-1 rounded-md bg-surface-success p-3 text-center text-heading-sm font-semibold">
                {intl.formatMessage(messages.newNotification)}
              </h4>
            )}
            <SearchDatesPicker
              className="px-2 lg:px-0"
              isReturn={hadEndDate}
              departureDate={beginDate}
              value={endDate ? [beginDate, endDate] : beginDate}
              returnDate={endDate}
              onDatesChange={(newbeginDate, newendDate) => {
                onDatesChange(newbeginDate, newendDate);
                if (!hadEndDate && isMdScreen) {
                  setIsDepartureOpen(false);
                }
              }}
              maxDaysAhead={maxDaysAhead}
              showDoubleView={isMdScreen}
            />
            {!isMdScreen && (
              <DatePopoutFooter
                onClickButton={() => {
                  onBeginDateToggle(false);
                  setIsDepartureOpen(false);
                  setIsReturnOpen(false);
                }}
              />
            )}
          </DatePopout>
        </BlurAwayListener>
        <span className="flex h-6 min-w-px flex-0 bg-text-secondary opacity-10" />
        <BlurAwayListener
          className="flex items-center"
          blurAfterClick
          onBlurAway={() => {
            if (isMdScreen) {
              setIsReturnOpen(false);
              onEndDateToggle(false);
            }
          }}
        >
          <DateInput
            date={endDate}
            text={intl.formatMessage(messages.placeholder)}
            isOpen={isReturnOpen}
            label={endLabel}
            buttonProps={{
              "aria-label": promptEndText,
              onPress: isMdScreen
                ? () => {
                    setIsReturnOpen((prev) => {
                      onEndDateToggle(!prev);
                      return !prev;
                    });
                    setBlinkDeparture(false);
                  }
                : presetReturnAndOpenDatePicker,
            }}
            blink={false}
          />

          {endDate && (
            <AriaButton
              className="z-2 -ml-8 inline cursor-pointer rounded-full p-2 text-neutral-alpha-1100 transition-transform hover:scale-120 hover:text-neutral-alpha-1300 focus-visible:outline-1"
              onPress={() => {
                onDatesChange(beginDate, undefined);
                setHadEndDate(false);
                onRemoveEndDate?.();
              }}
            >
              <XCircleSolid className="h-4 w-4" />
            </AriaButton>
          )}

          <DatePopout
            anchor="center"
            className="top-full"
            label={promptEndText}
            isOpen={isReturnOpen}
          >
            {!isMdScreen && (
              <DatePopoutHeader {...datePopoutHeaderProps} inputOpen="return" />
            )}
            <SearchDatesPicker
              className="px-2 lg:px-0"
              isReturn={isMdScreen}
              value={isMdScreen ? beginDate : [beginDate, endDate ?? null]}
              departureDate={beginDate}
              onDatesChange={isMdScreen ? onDatesChange : () => {}}
              maxDaysAhead={maxDaysAhead}
              minDate={isMdScreen ? undefined : beginDate}
              returnDate={endDate}
              onClickDay={(value, event) => {
                onSelectEndDate?.(value, event);
                if (isMdScreen) {
                  setIsReturnOpen(false);
                }
                setHadEndDate(true);
              }}
              showDoubleView={isMdScreen}
            />
            {!isMdScreen && (
              <DatePopoutFooter
                onClickButton={() => {
                  onEndDateToggle(false);
                  setIsDepartureOpen(false);
                  setIsReturnOpen(false);
                }}
              />
            )}
          </DatePopout>
        </BlurAwayListener>
        <span className="flex h-6 min-w-px flex-0 bg-text-secondary opacity-10" />

        {passengers && onPassengerChange && onPassengerToggle && (
          <BlurAwayListener
            blurAfterClick
            className="relative flex w-15 self-stretch"
            onBlurAway={() => {
              if (isMdScreen) {
                setIsPassengerPickerOpen(false);
                onPassengerToggle(false);
              }
            }}
          >
            <AriaButton
              aria-label={intl.formatMessage(
                passengerPickerMessage.passengerInfoHeader,
              )}
              className={tw(
                "flex h-full w-full cursor-pointer items-center justify-center gap-1.5 self-stretch rounded-full bg-neutral-700 p-2 py-2 text-label-md hover:bg-neutral-800 focus-visible:outline-1 lg:gap-2 lg:text-label-lg",
                isPassengerPickerOpen && "bg-neutral-900",
              )}
              onPress={() => {
                setIsPassengerPickerOpen((prev) => {
                  onPassengerToggle(!prev);
                  return !prev;
                });
              }}
            >
              <PassengerIcon className="h-4 opacity-50" />
              {passengerDetails && <span>{passengerDetails.length}</span>}
            </AriaButton>
            <PassengerPickerPopout
              handleCloseButtonClick={() => {
                setIsPassengerPickerOpen(false);
                onPassengerToggle(false);
              }}
              label={intl.formatMessage(
                passengerPickerMessage.passengerInfoHeader,
              )}
              isOpen={isPassengerPickerOpen}
            >
              <PassengerPickerForm
                passengers={passengers}
                onChange={setPassengers}
              />
              <div className="fixed bottom-0 left-0 z-10 w-full border-t border-t-border bg-white p-4 lg:relative lg:mt-0 lg:border-0 lg:p-0">
                <Button
                  className="mt-4 w-full lg:mt-1"
                  size="large"
                  onPress={() => {
                    sendAnalyticsInteractionEvent({
                      category: "SegmentPassengerPicker",
                      action: "Click:Confirm",
                      label: "NumberOfPassengers",
                      value: passengers.length,
                    });
                    onPassengerChange(passengers);
                    setIsPassengerPickerOpen(false);
                    onPassengerToggle(false);
                  }}
                  isDisabled={!isValidPassengerDetails(passengers)}
                >
                  {intl.formatMessage(passengerPickerMessage.confirm)}
                </Button>
              </div>
            </PassengerPickerPopout>
          </BlurAwayListener>
        )}
      </div>
    </>
  );
}
